/**
 * «Три тела» для индикатора загрузки: три планеты на вытянутых кеплеровых
 * орбитах вокруг ядра, как электроны в знаке атома.
 *
 * Каждая орбита — эллипс с ядром в фокусе (эксцентриситет `eccentricity`):
 * у ядра планета разгоняется, вдали — зависает, как по закону площадей.
 * Перицентр — в полтора радиуса ядра, апоцентр — в четыре с лишним: планеты
 * облетают ядро, уходят за него и выходят перед ним, но сквозь не проходят —
 * над диском ядра планета всегда явно за ним или явно перед ним.
 *
 * Таблица — одна орбита в её собственной плоскости (большая полуось 1, фокус в
 * нуле), 240 точек равномерно по ВРЕМЕНИ: решение уравнения Кеплера
 * M = E − e·sin E, поэтому неравномерность скорости уже в таблице. Три орбиты —
 * та же таблица, повёрнутая в пространстве: наклон `inclination`, перицентр
 * `periapsis`, узлы через 120° вокруг взгляда; планеты идут со сдвигом в треть
 * цикла. Вся система покачивается вокруг вертикали на ±`sway` раз за цикл. Та
 * же таблица — в приставке (`ui/ThreeBody.kt`); генератор —
 * `tools/three-body/kepler.py` в артефактах редизайна.
 *
 * Глубина: позиция с мягкой перспективой (`distance`), а размер и яркость —
 * с сильной: дальняя планета втрое меньше ближней (`SIZE_FAR`…`SIZE_NEAR`).
 */

/** x, y орбиты в её плоскости (a = 1, ядро в фокусе), 240 точек по времени; 0 — перицентр. */
export const KEPLER_ORBIT: readonly number[] = [
  0.5200, 0.0000, 0.5187, 0.0441, 0.5150, 0.0880, 0.5087, 0.1315, 0.5000, 0.1744, 0.4891, 0.2165,
  0.4759, 0.2576, 0.4607, 0.2976, 0.4436, 0.3363, 0.4247, 0.3738, 0.4042, 0.4098, 0.3823, 0.4443,
  0.3590, 0.4773, 0.3346, 0.5088, 0.3092, 0.5388, 0.2829, 0.5672, 0.2558, 0.5941, 0.2281, 0.6195,
  0.1998, 0.6434, 0.1711, 0.6658, 0.1421, 0.6869, 0.1127, 0.7066, 0.0831, 0.7249, 0.0534, 0.7420,
  0.0236, 0.7579, -0.0062, 0.7726, -0.0360, 0.7861, -0.0658, 0.7985, -0.0955, 0.8098, -0.1251, 0.8202,
  -0.1545, 0.8295, -0.1837, 0.8379, -0.2128, 0.8454, -0.2416, 0.8520, -0.2702, 0.8577, -0.2985, 0.8627,
  -0.3266, 0.8669, -0.3544, 0.8703, -0.3819, 0.8730, -0.4091, 0.8751, -0.4359, 0.8764, -0.4625, 0.8771,
  -0.4887, 0.8772, -0.5146, 0.8767, -0.5402, 0.8757, -0.5654, 0.8741, -0.5903, 0.8719, -0.6149, 0.8693,
  -0.6391, 0.8661, -0.6629, 0.8625, -0.6864, 0.8584, -0.7095, 0.8539, -0.7323, 0.8489, -0.7547, 0.8435,
  -0.7768, 0.8377, -0.7985, 0.8316, -0.8198, 0.8251, -0.8408, 0.8182, -0.8614, 0.8109, -0.8817, 0.8034,
  -0.9016, 0.7955, -0.9212, 0.7873, -0.9404, 0.7788, -0.9593, 0.7700, -0.9778, 0.7609, -0.9959, 0.7515,
  -1.0137, 0.7419, -1.0312, 0.7320, -1.0483, 0.7219, -1.0650, 0.7115, -1.0814, 0.7009, -1.0975, 0.6900,
  -1.1132, 0.6790, -1.1286, 0.6677, -1.1436, 0.6563, -1.1583, 0.6446, -1.1726, 0.6328, -1.1866, 0.6208,
  -1.2003, 0.6085, -1.2136, 0.5962, -1.2266, 0.5836, -1.2393, 0.5709, -1.2516, 0.5581, -1.2636, 0.5451,
  -1.2752, 0.5319, -1.2865, 0.5186, -1.2975, 0.5052, -1.3082, 0.4917, -1.3185, 0.4780, -1.3285, 0.4642,
  -1.3382, 0.4503, -1.3476, 0.4363, -1.3566, 0.4222, -1.3653, 0.4079, -1.3737, 0.3936, -1.3817, 0.3792,
  -1.3895, 0.3647, -1.3969, 0.3501, -1.4040, 0.3355, -1.4108, 0.3207, -1.4172, 0.3059, -1.4234, 0.2910,
  -1.4292, 0.2761, -1.4347, 0.2611, -1.4399, 0.2460, -1.4447, 0.2309, -1.4493, 0.2157, -1.4535, 0.2005,
  -1.4574, 0.1853, -1.4611, 0.1700, -1.4643, 0.1546, -1.4673, 0.1393, -1.4700, 0.1239, -1.4723, 0.1084,
  -1.4744, 0.0930, -1.4761, 0.0775, -1.4775, 0.0620, -1.4786, 0.0465, -1.4794, 0.0310, -1.4798, 0.0155,
  -1.4800, 0.0000, -1.4798, -0.0155, -1.4794, -0.0310, -1.4786, -0.0465, -1.4775, -0.0620, -1.4761, -0.0775,
  -1.4744, -0.0930, -1.4723, -0.1084, -1.4700, -0.1239, -1.4673, -0.1393, -1.4643, -0.1546, -1.4611, -0.1700,
  -1.4574, -0.1853, -1.4535, -0.2005, -1.4493, -0.2157, -1.4447, -0.2309, -1.4399, -0.2460, -1.4347, -0.2611,
  -1.4292, -0.2761, -1.4234, -0.2910, -1.4172, -0.3059, -1.4108, -0.3207, -1.4040, -0.3355, -1.3969, -0.3501,
  -1.3895, -0.3647, -1.3817, -0.3792, -1.3737, -0.3936, -1.3653, -0.4079, -1.3566, -0.4222, -1.3476, -0.4363,
  -1.3382, -0.4503, -1.3285, -0.4642, -1.3185, -0.4780, -1.3082, -0.4917, -1.2975, -0.5052, -1.2865, -0.5186,
  -1.2752, -0.5319, -1.2636, -0.5451, -1.2516, -0.5581, -1.2393, -0.5709, -1.2266, -0.5836, -1.2136, -0.5962,
  -1.2003, -0.6085, -1.1866, -0.6208, -1.1726, -0.6328, -1.1583, -0.6446, -1.1436, -0.6563, -1.1286, -0.6677,
  -1.1132, -0.6790, -1.0975, -0.6900, -1.0814, -0.7009, -1.0650, -0.7115, -1.0483, -0.7219, -1.0312, -0.7320,
  -1.0137, -0.7419, -0.9959, -0.7515, -0.9778, -0.7609, -0.9593, -0.7700, -0.9404, -0.7788, -0.9212, -0.7873,
  -0.9016, -0.7955, -0.8817, -0.8034, -0.8614, -0.8109, -0.8408, -0.8182, -0.8198, -0.8251, -0.7985, -0.8316,
  -0.7768, -0.8377, -0.7547, -0.8435, -0.7323, -0.8489, -0.7095, -0.8539, -0.6864, -0.8584, -0.6629, -0.8625,
  -0.6391, -0.8661, -0.6149, -0.8693, -0.5903, -0.8719, -0.5654, -0.8741, -0.5402, -0.8757, -0.5146, -0.8767,
  -0.4887, -0.8772, -0.4625, -0.8771, -0.4359, -0.8764, -0.4091, -0.8751, -0.3819, -0.8730, -0.3544, -0.8703,
  -0.3266, -0.8669, -0.2985, -0.8627, -0.2702, -0.8577, -0.2416, -0.8520, -0.2128, -0.8454, -0.1837, -0.8379,
  -0.1545, -0.8295, -0.1251, -0.8202, -0.0955, -0.8098, -0.0658, -0.7985, -0.0360, -0.7861, -0.0062, -0.7726,
  0.0236, -0.7579, 0.0534, -0.7420, 0.0831, -0.7249, 0.1127, -0.7066, 0.1421, -0.6869, 0.1711, -0.6658,
  0.1998, -0.6434, 0.2281, -0.6195, 0.2558, -0.5941, 0.2829, -0.5672, 0.3092, -0.5388, 0.3346, -0.5088,
  0.3590, -0.4773, 0.3823, -0.4443, 0.4042, -0.4098, 0.4247, -0.3738, 0.4436, -0.3363, 0.4607, -0.2976,
  0.4759, -0.2576, 0.4891, -0.2165, 0.5000, -0.1744, 0.5087, -0.1315, 0.5150, -0.0880, 0.5187, -0.0441,
];

export const ORBIT_POINTS = KEPLER_ORBIT.length / 2;

/** Геометрия сцены в единицах поля знака (100 × 100, ядро в центре). */
export const THREE_BODY = {
  /** Полный цикл, мс: один оборот каждой планеты, вдох ядра и покачивание. */
  cycleMs: 5400,
  /** Большая полуось: апоцентр 31·1,48 ≈ 46 — в пределах поля знака. */
  semiMajor: 31,
  eccentricity: 0.48,
  /** Расстояние до камеры для позиций — перспектива мягкая, поле не распирает. */
  distance: 170,
  /** Наклон плоскостей орбит к экрану, градусы: чем больше, тем глубже заходят за ядро. */
  inclination: 65,
  /** Поворот перицентра в плоскости орбиты, градусы. */
  periapsis: 20,
  /** Узел первой орбиты вокруг взгляда, градусы; остальные — через 120°. */
  node: 90,
  /** Покачивание системы вокруг вертикали за цикл, ± градусы. */
  sway: 10,
} as const;

/** Размер планеты на дальнем и ближнем краю глубины (доля спрайта). */
export const SIZE_FAR = 0.42;
export const SIZE_NEAR = 1.15;
/** Глубина, которой нормируются размер и яркость. */
export const DEPTH_MAX = 30;

const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Матрицы трёх орбит: узел (вокруг взгляда) · наклон (вокруг горизонтали) · перицентр. */
const ORBIT_FRAMES: readonly (readonly number[])[] = [0, 1, 2].map((k) => {
  const o = rad(THREE_BODY.node + 120 * k);
  const i = rad(THREE_BODY.inclination);
  const w = rad(THREE_BODY.periapsis);
  const [co, so, ci, si, cw, sw] = [Math.cos(o), Math.sin(o), Math.cos(i), Math.sin(i), Math.cos(w), Math.sin(w)];
  // Столбцы — образы осей x и y плоскости орбиты (z плоскости не нужна).
  return [
    co * cw - so * ci * sw, -co * sw - so * ci * cw,
    so * cw + co * ci * sw, -so * sw + co * ci * cw,
    si * sw, si * cw,
  ];
});

/**
 * Планета `planet` (0…2) на фазе `phase` (доли цикла, любое число) в проекции:
 * out = [x, y, z, s] — x, y в единицах поля от центра (y вниз), z — к зрителю,
 * s — размер планеты (доля спрайта, по глубине). `swayDeg` — покачивание.
 */
export function projectOrbit(planet: number, phase: number, swayDeg: number, out: Float64Array, at = 0): void {
  const p = (phase - Math.floor(phase)) * ORBIT_POINTS;
  const i = Math.floor(p) % ORBIT_POINTS;
  const k = p - Math.floor(p);
  const j = (i + 1) % ORBIT_POINTS;
  const a = THREE_BODY.semiMajor;
  const ox = (KEPLER_ORBIT[2 * i]! + (KEPLER_ORBIT[2 * j]! - KEPLER_ORBIT[2 * i]!) * k) * a;
  const oy = (KEPLER_ORBIT[2 * i + 1]! + (KEPLER_ORBIT[2 * j + 1]! - KEPLER_ORBIT[2 * i + 1]!) * k) * a;
  const m = ORBIT_FRAMES[planet]!;
  const x0 = m[0]! * ox + m[1]! * oy;
  const y0 = m[2]! * ox + m[3]! * oy;
  const z0 = m[4]! * ox + m[5]! * oy;
  // Покачивание вокруг вертикали (y — вверх, z — к зрителю).
  const g = rad(swayDeg);
  const x = x0 * Math.cos(g) + z0 * Math.sin(g);
  const z = -x0 * Math.sin(g) + z0 * Math.cos(g);
  const f = THREE_BODY.distance / (THREE_BODY.distance - z);
  const near = Math.min(1, Math.max(0, (z + DEPTH_MAX) / (2 * DEPTH_MAX)));
  out[at] = x * f;
  out[at + 1] = -y0 * f;
  out[at + 2] = z;
  out[at + 3] = SIZE_FAR + (SIZE_NEAR - SIZE_FAR) * near;
}

/** Покачивание системы в момент `t` (доли цикла). */
export function swayAt(t: number): number {
  return THREE_BODY.sway * Math.sin(2 * Math.PI * t);
}
